import React, { createContext, useContext, useMemo } from "react";

const FINANCIAL = "financial_sensitive";
const GENERAL = "general";
const FULL_VIEW_ROLES = new Set(["admin", "editor", "viewer"]);

// Markets Strategy and Guidance expose strategic policy data. Keep this
// capability decision in the shared client access-policy surface so tabs,
// redirects, remembered-tab state, and fetch gates use the same role rule.
export const MARKETS_ADMIN_TAB_KEYS = Object.freeze(["strategy", "recommendations"]);

export function canAccessMarketsStrategy(role) {
  return role === "admin";
}

export function canonicalizeMarketsTab(tab, role, strategyTabsEnabled = true) {
  return MARKETS_ADMIN_TAB_KEYS.includes(tab) &&
    (!canAccessMarketsStrategy(role) || !strategyTabsEnabled)
    ? "plan"
    : tab;
}

export const ROLE_DISPLAY_LABELS = Object.freeze({
  admin: "Admin",
  editor: "Editor",
  viewer: "Viewer",
  view_limited: "Viewer",
});

export function getRoleDisplayLabel(role) {
  return ROLE_DISPLAY_LABELS[role] ?? "Unknown";
}

export function createClientSensitivityPolicy({ role, taxonomy, ready = true } = {}) {
  const safeTaxonomy =
    taxonomy && typeof taxonomy === "object" && !Array.isArray(taxonomy)
      ? taxonomy
      : null;
  const areas =
    safeTaxonomy?.areas && typeof safeTaxonomy.areas === "object" && !Array.isArray(safeTaxonomy.areas)
      ? { ...safeTaxonomy.areas }
      : {};
  const keySegments = Array.isArray(safeTaxonomy?.defaults?.financialByNature?.keySegments)
    ? [...safeTaxonomy.defaults.financialByNature.keySegments]
    : [];
  const policyReady = Boolean(ready && safeTaxonomy);

  function resolveSensitivity(areaKey) {
    if (!policyReady || typeof areaKey !== "string" || areaKey.length === 0) return FINANCIAL;
    const configured = areas[areaKey];
    if (configured === GENERAL || configured === FINANCIAL) return configured;
    const parts = areaKey.split(".");
    return keySegments.some(
      (segment) => typeof segment === "string" && parts.some((part) => part.includes(segment)),
    )
      ? FINANCIAL
      : FINANCIAL;
  }

  function canSee(areaKey) {
    if (!policyReady) return false;
    if (role === "view_limited") return resolveSensitivity(areaKey) === GENERAL;
    return FULL_VIEW_ROLES.has(role);
  }

  return Object.freeze({
    ready: policyReady,
    role,
    canSee,
    resolveSensitivity,
    taxonomy: policyReady ? { ...safeTaxonomy, areas, defaults: {
      ...safeTaxonomy.defaults,
      financialByNature: {
        ...safeTaxonomy.defaults?.financialByNature,
        keySegments,
      },
    } } : null,
  });
}

const DENY_ALL_POLICY = createClientSensitivityPolicy();
const SensitivityPolicyContext = createContext(DENY_ALL_POLICY);

export function SensitivityPolicyProvider({ role, taxonomy, ready = true, children }) {
  const value = useMemo(
    () => createClientSensitivityPolicy({ role, taxonomy, ready }),
    [role, taxonomy, ready],
  );
  return (
    <SensitivityPolicyContext.Provider value={value}>
      {children}
    </SensitivityPolicyContext.Provider>
  );
}

export function useSensitivityPolicy() {
  return useContext(SensitivityPolicyContext);
}